import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const NodeJsCourse = () => {
  const testimonials = [
    {
      id: 1,
      name: "Rishabh Singh",
      review:
        "The Node.js course was phenomenal! I learned to build powerful backend APIs and understood asynchronous programming much better. The practical projects were incredibly helpful for my portfolio.",
      rating: 5,
      date: "2024-07-05",
      profileImg: "https://randomuser.me/api/portraits/men/36.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Sneha Reddy",
      review:
        "Coming from a frontend background, this course made backend development approachable. Express.js and MongoDB integration were taught excellently. Highly recommend for full-stack aspiring developers.",
      rating: 4,
      date: "2024-07-03",
      profileImg: "https://randomuser.me/api/portraits/women/49.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Gaurav Malik",
      review:
        "The best Node.js training in Noida. The instructors are real experts, and the focus on microservices and real-time applications using WebSockets was crucial for my current job.",
      rating: 5,
      date: "2024-06-30",
      profileImg: "https://randomuser.me/api/portraits/men/54.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Ananya Dixit",
      review:
        "I appreciated the deep dive into authentication (JWT) and error handling. The course structure is logical, and the support staff is always ready to help. Got a backend developer role quickly after!",
      rating: 5,
      date: "2024-06-28",
      profileImg: "https://randomuser.me/api/portraits/women/64.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Karan Johar",
      review:
        "Fantastic coverage of Node.js from basics to advanced topics like unit testing and deployment. It truly prepares you for building scalable backend systems.",
      rating: 4,
      date: "2024-06-25",
      profileImg: "https://randomuser.me/api/portraits/men/79.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

   const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is Node.js and why is it important?",
      answer:
        "Node.js is an open-source, cross-platform, JavaScript runtime environment that allows developers to execute JavaScript code outside of a web browser. It's crucial for building scalable, high-performance, and real-time network applications, RESTful APIs, and microservices due to its non-blocking, event-driven architecture.",
    },
    {
      title: "What are the prerequisites for this Node.js course?",
      answer:
        "A strong understanding of **JavaScript (ES6+)** is essential. Familiarity with command-line interfaces and basic web concepts (HTTP, APIs) is also highly recommended. Our course builds upon your JavaScript knowledge to introduce you to server-side development.",
    },
    {
      title: "What key concepts will I learn in this Node.js course?",
      answer:
        "You will learn Node.js core modules, event loop, asynchronous programming (callbacks, Promises, Async/Await), Express.js framework, working with databases (MongoDB, SQL), building RESTful APIs, authentication (JWT), error handling, testing, and deployment strategies.",
    },
    {
      title: "Is Node.js in high demand in the job market?",
      answer:
        "Yes, Node.js developers are in very high demand. Companies worldwide use Node.js for backend development, especially for scalable and modern web applications, microservices, and real-time features. It's a highly valued skill for backend and full-stack roles.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Absolutely! We provide comprehensive placement assistance for our Node.js graduates. This includes resume optimization, mock interviews, soft skills training, and connecting you with our extensive network of hiring companies for Backend Developer, Node.js Developer, and Full Stack Developer positions in Noida and beyond.",
    },
    {
      title: "What kind of projects will I build during the training?",
      answer:
        "You will develop several practical projects, culminating in a major Capstone Project. Examples include building a complete RESTful API for an e-commerce platform, a chat application using WebSockets, or a task management system, all designed to showcase your backend development skills.",
    },
    {
      title: "What are the career opportunities after this Node.js training?",
      answer:
        "Graduates can pursue roles such as Node.js Developer, Backend Developer, API Developer, Full Stack Developer, Software Engineer, and Microservices Developer.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace (intensive fast-track vs. regular). We offer both immersive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your learning preferences.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Node.js & JavaScript Fundamentals",
      description: "Understanding Node.js architecture, setting up the environment, event loop, V8 engine, and a quick refresher on advanced JavaScript concepts (callbacks, promises, async/await).",
    },
    {
      title: "Module 2: Node.js Core Modules",
      description: "Working with `fs` (file system), `http`, `path`, `url`, `events`, and `stream` modules for foundational backend operations.",
    },
    {
      title: "Module 3: Express.js Framework",
      description: "Building web applications and RESTful APIs with Express.js: routing, middleware, handling requests/responses, and template engines.",
    },
    {
      title: "Module 4: Working with Databases (MongoDB & Mongoose)",
      description: "Connecting Node.js/Express.js to MongoDB, performing CRUD operations, understanding Mongoose ODM for schema definition and validation.",
    },
    {
      title: "Module 5: RESTful API Design & Development",
      description: "Designing robust APIs, implementing CRUD operations, API versioning, error handling, and using Postman for API testing.",
    },
    {
      title: "Module 6: Authentication & Authorization (JWT)",
      description: "Implementing user registration, login, session management, and securing APIs using JSON Web Tokens (JWT) and Passport.js.",
    },
    {
      title: "Module 7: Asynchronous Programming Deep Dive",
      description: "Mastering Promises, Async/Await for cleaner asynchronous code, and understanding the Event Emitter pattern for custom events.",
    },
    {
      title: "Module 8: Error Handling & Debugging",
      description: "Implementing effective error handling strategies, global error middleware, and debugging Node.js applications.",
    },
    {
      title: "Module 9: Real-time Applications (WebSockets)",
      description: "Building real-time features like chat applications using WebSockets with Socket.IO.",
    },
    {
      title: "Module 10: Testing Node.js Applications",
      description: "Introduction to unit testing with Mocha/Chai or Jest, writing tests for routes, models, and middleware.",
    },
    {
      title: "Module 11: Deployment & Production Best Practices",
      description: "Preparing Node.js applications for production, deployment to cloud platforms (Heroku, AWS EC2/Elastic Beanstalk), PM2 for process management, and security best practices.",
    },
    {
      title: "Module 12: Capstone Project (Full-fledged Backend Application)",
      description: "Develop a complete, scalable backend application from scratch using Node.js, Express, and a database, integrating all learned concepts. This project will be a significant addition to your professional portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Node.js Course in Noida | Master Backend Development</title>
        <meta
          name="description"
          content="Enroll in the best Node.js training in Noida. Learn Express.js, MongoDB, build REST APIs, and master backend development with 100% placement support."
        />
        <meta
          name="keywords"
          content="Node.js training Noida, Node.js course, learn Node.js, Backend Development, Express.js, MongoDB, REST API, JavaScript backend, Node.js certification, Full Stack Development"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://rootinfosol.com/sites/default/files/2018-03/Nodejs.jpg"
          alt="Node.js Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Master <span className="text-blue-400">Node.js Development</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Build high-performance, scalable backend applications and RESTful APIs with our expert-led Node.js training.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-blue-700 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
               <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("NodeJs Course Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn Node.js Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn Node.js?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Node.js has become a cornerstone of modern web development, enabling full-stack JavaScript development and powering fast, scalable applications. It's the ideal choice for building APIs, microservices, and real-time applications.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-green-700 mb-3">Full-Stack JavaScript</h3>
              <p className="text-gray-600">Use JavaScript for both frontend and backend development, streamlining workflows.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-green-700 mb-3">Scalability & Performance</h3>
              <p className="text-gray-600">Built on Chrome's V8 engine, known for its speed and non-blocking I/O.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-green-700 mb-3">Vast Ecosystem (NPM)</h3>
              <p className="text-gray-600">Access to millions of open-source libraries and modules via NPM.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Hands-on API Development', desc: 'Build robust RESTful APIs and backend services from scratch.' },
              { icon: '💡', title: 'Master Express.js & MongoDB', desc: 'In-depth learning of the most popular Node.js framework and NoSQL database.' },
              { icon: '👨‍🏫', title: 'Expert-Led Training', desc: 'Learn from experienced backend and full-stack developers.' },
              { icon: '🎓', title: 'Industry-Recognized Certification', desc: 'Receive a professional certificate to enhance your resume.' },
              { icon: '🤝', title: 'Dedicated Placement Support', desc: 'Comprehensive assistance for resume building, mock interviews, and job placements.' },
              { icon: '🔄', title: 'Flexible Learning Modes', desc: 'Choose between dynamic offline classroom sessions or interactive online live classes.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-purple-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-purple-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Node.js training ensures you gain proficiency in all essential tools and concepts for building scalable backend applications.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'Node.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
              { name: 'Express.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' },
              { name: 'JavaScript (ES6+)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
              { name: 'MongoDB', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
              { name: 'Mongoose', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongoose/mongoose-original.svg' },
              { name: 'REST APIs', logo: 'https://xano.ghost.io/content/images/2022/09/when-is-it-best-to-use-a-restful-api.png' }, // Generic API icon
              { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
              { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
              { name: 'Postman', logo: 'https://w7.pngwing.com/pngs/28/245/png-transparent-postman-hd-logo-thumbnail.png' }, // Generic API icon, Postman is for testing it
              { name: 'NPM/Yarn', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/npm/npm-original-wordmark.svg' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A Node.js certification from us will prepare you for a variety of in-demand backend and full-stack development roles.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '💻', title: 'Node.js Developer', desc: 'Specialize in building robust and scalable backend systems with Node.js.' },
              { icon: '🌐', title: 'Backend Developer', desc: 'Focus on server-side logic, database interactions, and API development.' },
              { icon: '✨', title: 'Full Stack Developer', desc: 'Bridge the gap between frontend (e.g., React/Angular) and Node.js backend.' },
              { icon: '🛠️', title: 'API Developer', desc: 'Design and implement high-performance RESTful and GraphQL APIs.' },
              { icon: '📈', title: 'Software Engineer', desc: 'Contribute to the entire software development lifecycle with a focus on backend.' },
              { icon: '💡', title: 'Microservices Developer', desc: 'Build and deploy modular, independent services using Node.js.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-purple-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Node.js Development course.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Node.js Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-green-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-green-900">
            What Our Node.js Development Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-green-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-green-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-green-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default NodeJsCourse;